import { useCallback, useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import type { GoogleUser } from '../types/auth';

interface TaskAssigneeProps {
  assignedToAI: boolean;
  assignedToTenantID: string;
  workstreamID: string;
  taskID: string;
  version: number;
  onSaved?: (updated: { AssignedToAI: boolean; AssignedToTenantID: string; Version: number }) => void;
  onError?: (message: string) => void;
  // Compact mode hides the text label and renders only a 28px avatar circle.
  compact?: boolean;
}

function RobotIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="11" width="18" height="10" rx="2" />
      <circle cx="12" cy="5" r="2" />
      <path d="M12 7v4" />
      <line x1="8" y1="16" x2="8" y2="16" strokeWidth="3" strokeLinecap="round" />
      <line x1="16" y1="16" x2="16" y2="16" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function QuestionIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function getCurrentUser(): GoogleUser | null {
  const userStr = Cookies.get('User');
  if (!userStr) return null;
  try {
    return JSON.parse(userStr) as GoogleUser;
  } catch {
    return null;
  }
}

function getCurrentTenantID(): string {
  return Cookies.get('TenantID') ?? '';
}

type AssigneeKind = 'ai' | 'user' | 'unassigned';

function getAssigneeKind(assignedToAI: boolean, assignedToTenantID: string): AssigneeKind {
  if (assignedToAI) return 'ai';
  if (assignedToTenantID) return 'user';
  return 'unassigned';
}

export default function TaskAssignee({
  assignedToAI,
  assignedToTenantID,
  workstreamID,
  taskID,
  version,
  onSaved,
  onError,
  compact = false,
}: TaskAssigneeProps) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const user = getCurrentUser();
  const currentTenantID = getCurrentTenantID();

  const kind = getAssigneeKind(assignedToAI, assignedToTenantID);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [open]);

  const handleSelect = useCallback(
    async (newKind: AssigneeKind) => {
      if (newKind === kind) {
        setOpen(false);
        return;
      }
      setSaving(true);
      setOpen(false);
      try {
        const fields: Record<string, unknown> = {};

        if (newKind === 'ai') {
          fields.AssignedToAI = true;
          fields.ClearAssignedToTenantID = true;
          fields.AutoPause = true;
        } else if (newKind === 'user') {
          fields.AssignedToAI = false;
          fields.AssignedToTenantID = currentTenantID;
        } else {
          fields.AssignedToAI = false;
          fields.ClearAssignedToTenantID = true;
        }

        const resp = await fetch(
          `/api/workstreams/${encodeURIComponent(workstreamID)}/tasks/${encodeURIComponent(taskID)}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ Version: version, Fields: fields }),
          },
        );
        if (resp.ok) {
          const data = await resp.json();
          onSaved?.({
            AssignedToAI: data.AssignedToAI ?? false,
            AssignedToTenantID: data.AssignedToTenantID ?? '',
            Version: data.Version,
          });
        } else {
          const text = await resp.text();
          onError?.(text || 'Failed to update assignee');
        }
      } catch (err: unknown) {
        onError?.(err instanceof Error ? err.message : 'Failed to update assignee');
      } finally {
        setSaving(false);
      }
    },
    [kind, currentTenantID, workstreamID, taskID, version, onSaved, onError],
  );

  const renderAvatar = (k: AssigneeKind, size = 24) => {
    if (k === 'ai') return <RobotIcon size={size} />;
    if (k === 'user' && user?.picture) {
      return (
        <img
          src={user.picture}
          alt=""
          style={{ width: size, height: size, borderRadius: '50%', display: 'block' }}
        />
      );
    }
    return <QuestionIcon size={size} />;
  };

  const renderLabel = (k: AssigneeKind) => {
    if (k === 'ai') return 'Plan 42';
    if (k === 'user') return user?.name ?? 'You';
    return 'Unassigned';
  };

  return (
    <div ref={menuRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        disabled={saving}
        title={compact ? renderLabel(kind) : undefined}
        aria-label={compact ? `Assignee: ${renderLabel(kind)}` : undefined}
        style={
          compact
            ? {
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: '#e1e4e8',
                color: '#57606a',
                border: 'none',
                padding: 0,
                cursor: saving ? 'wait' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                overflow: 'hidden',
              }
            : {
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: 'none',
                border: 'none',
                padding: '4px 8px',
                cursor: saving ? 'wait' : 'pointer',
                fontSize: '14px',
                color: '#24292f',
                borderRadius: '6px',
              }
        }
      >
        {renderAvatar(kind, compact ? 18 : 24)}
        {!compact && <span>{renderLabel(kind)}</span>}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            background: '#fff',
            border: '1px solid #ccc',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            minWidth: '180px',
            overflow: 'hidden',
            zIndex: 1000,
          }}
        >
          {(['user', 'ai', 'unassigned'] as AssigneeKind[]).filter((k) => k !== 'user' || currentTenantID).map((k, index) => (
            <button
              key={k}
              type="button"
              onClick={() => handleSelect(k)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '8px 12px',
                background: k === kind ? '#f0f0f0' : '#fff',
                border: 'none',
                borderTop: index === 0 ? 'none' : '1px solid #eee',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#24292f',
                textAlign: 'left',
                borderRadius: 0,
              }}
            >
              {renderAvatar(k, 20)}
              <span>{renderLabel(k)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
